// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  Button,
  Table,
  Icon,
  Message,
  Loader,
  Dropdown,
  Label,
  Segment,
  Form,
  Pagination,
  Input
} from 'semantic-ui-react';
import { auditApi } from '../api/auditApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import {
  AuditEvent,
  AuditQueryFilters,
  PageResponse,
  AuditCategory,
  AuditAction,
  AuditSeverity,
  ExportFormat
} from '../types/audit';
import AuditEventDetail from '../components/AuditEventDetail';
import { formatDateTime } from '@loa/theme';

function AuditLogViewerPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [pageInfo, setPageInfo] = useState<PageResponse<AuditEvent> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [selectedBrokerage, setSelectedBrokerage] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [resourceType, setResourceType] = useState<string>('');
  const [resourceId, setResourceId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;

  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

  // Load brokerages for filter
  useEffect(() => {
    loadBrokerages();
  }, []);

  const loadBrokerages = async () => {
    try {
      const data = await brokerageApi.getAll();
      setBrokerages(data.content);
    } catch (err) {
      console.error('Failed to load brokerages:', err);
    }
  };

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const filters: AuditQueryFilters = {
        brokerageId: selectedBrokerage || undefined,
        category: selectedCategory || undefined,
        action: selectedAction || undefined,
        severity: selectedSeverity || undefined,
        userId: userId || undefined,
        resourceType: resourceType || undefined,
        resourceId: resourceId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        searchText: searchText || undefined
      };

      const page = await auditApi.queryEvents(filters, {
        page: currentPage,
        size: pageSize,
        sort: 'timestamp,desc'
      });

      setEvents(page.content);
      setPageInfo(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit events');
    } finally {
      setLoading(false);
    }
  }, [
    selectedBrokerage,
    selectedCategory,
    selectedAction,
    selectedSeverity,
    userId,
    resourceType,
    resourceId,
    startDate,
    endDate,
    searchText,
    currentPage,
    pageSize
  ]);

  // Load events when filters or page changes
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const clearFilters = () => {
    setSelectedBrokerage('');
    setSelectedCategory('');
    setSelectedAction('');
    setSelectedSeverity('');
    setUserId('');
    setResourceType('');
    setResourceId('');
    setStartDate('');
    setEndDate('');
    setSearchText('');
    setCurrentPage(0);
  };

  const handleViewDetails = (event: AuditEvent) => {
    setSelectedEvent(event);
    setDetailModalOpen(true);
  };

  const handleExport = async (format: ExportFormat) => {
    try {
      setExporting(true);
      setError(null);

      const filters: Omit<AuditQueryFilters, 'searchText'> = {
        brokerageId: selectedBrokerage || undefined,
        category: selectedCategory || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      };

      const blob = await auditApi.exportEvents(format, filters);

      // Download file
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-export-${new Date().toISOString()}.${format.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      globalThis.URL.revokeObjectURL(url);
      a.remove();

      setSuccessMessage(`Audit events exported successfully as ${format}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export audit events');
    } finally {
      setExporting(false);
    }
  };

  const getSeverityColor = (severity: string): 'green' | 'yellow' | 'orange' | 'red' | 'grey' => {
    switch (severity) {
      case AuditSeverity.INFO:
        return 'green';
      case AuditSeverity.WARN:
        return 'yellow';
      case AuditSeverity.ERROR:
        return 'orange';
      case AuditSeverity.CRITICAL:
        return 'red';
      default:
        return 'grey';
    }
  };

  const brokerageOptions = [
    { key: '', text: 'All Brokerages', value: '' },
    ...brokerages.map((b) => ({ key: b.id, text: b.name, value: b.id }))
  ];

  const categoryOptions = [
    { key: '', text: 'All Categories', value: '' },
    ...Object.values(AuditCategory).map((cat) => ({
      key: cat,
      text: cat,
      value: cat
    }))
  ];

  const actionOptions = [
    { key: '', text: 'All Actions', value: '' },
    ...Object.values(AuditAction).map((action) => ({
      key: action,
      text: action,
      value: action
    }))
  ];

  const severityOptions = [
    { key: '', text: 'All Severities', value: '' },
    ...Object.values(AuditSeverity).map((sev) => ({
      key: sev,
      text: sev,
      value: sev
    }))
  ];

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='history' />
        <Header.Content>
          Audit Log Viewer
          <Header.Subheader>
            View and analyze platform audit events
          </Header.Subheader>
        </Header.Content>
      </Header>

      {/* Export Buttons */}
      <div style={{ marginBottom: '20px' }}>
        <Button.Group>
          <Button
            color='teal'
            onClick={() => handleExport('CSV')}
            loading={exporting}
            disabled={exporting}
          >
            <Icon name='download' />
            Export CSV
          </Button>
          <Button
            color='blue'
            onClick={() => handleExport('JSON')}
            loading={exporting}
            disabled={exporting}
          >
            <Icon name='download' />
            Export JSON
          </Button>
        </Button.Group>
      </div>

      {/* Filters */}
      <Segment>
        <Header as='h3'>
          <Icon name='filter' />
          Filters
        </Header>
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='filter-brokerage'>Brokerage</label>
              <Dropdown
                id='filter-brokerage'
                fluid
                selection
                search
                options={brokerageOptions}
                value={selectedBrokerage}
                onChange={(_e, data) => {
                  setSelectedBrokerage(data.value as string);
                  setCurrentPage(0);
                }}
                placeholder='Filter by brokerage'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-category'>Category</label>
              <Dropdown
                id='filter-category'
                fluid
                selection
                search
                options={categoryOptions}
                value={selectedCategory}
                onChange={(_e, data) => {
                  setSelectedCategory(data.value as string);
                  setCurrentPage(0);
                }}
                placeholder='Filter by category'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-action'>Action</label>
              <Dropdown
                id='filter-action'
                fluid
                selection
                search
                options={actionOptions}
                value={selectedAction}
                onChange={(_e, data) => {
                  setSelectedAction(data.value as string);
                  setCurrentPage(0);
                }}
                placeholder='Filter by action'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-severity'>Severity</label>
              <Dropdown
                id='filter-severity'
                fluid
                selection
                options={severityOptions}
                value={selectedSeverity}
                onChange={(_e, data) => {
                  setSelectedSeverity(data.value as string);
                  setCurrentPage(0);
                }}
                placeholder='Filter by severity'
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='filter-user-id'>User ID</label>
              <Input
                id='filter-user-id'
                fluid
                placeholder='Filter by user ID'
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-resource-type'>Resource Type</label>
              <Input
                id='filter-resource-type'
                fluid
                placeholder='Filter by resource type'
                value={resourceType}
                onChange={(e) => {
                  setResourceType(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-resource-id'>Resource ID</label>
              <Input
                id='filter-resource-id'
                fluid
                placeholder='Filter by resource ID'
                value={resourceId}
                onChange={(e) => {
                  setResourceId(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='filter-start-date'>Start Date</label>
              <input
                id='filter-start-date'
                type='datetime-local'
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-end-date'>End Date</label>
              <input
                id='filter-end-date'
                type='datetime-local'
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </Form.Field>
          </Form.Group>
          <Form.Field>
            <label htmlFor='filter-search-text'>Search Text</label>
            <Input
              id='filter-search-text'
              fluid
              icon='search'
              placeholder='Search in event descriptions...'
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(0);
              }}
            />
          </Form.Field>
          <Button size='small' onClick={clearFilters}>
            <Icon name='remove' />
            Clear Filters
          </Button>
        </Form>
      </Segment>

      {successMessage && (
        <Message positive onDismiss={() => setSuccessMessage(null)}>
          <Message.Header>Success</Message.Header>
          <p>{successMessage}</p>
        </Message>
      )}

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline='centered'>
          Loading audit events...
        </Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Timestamp</Table.HeaderCell>
                <Table.HeaderCell>Severity</Table.HeaderCell>
                <Table.HeaderCell>Category</Table.HeaderCell>
                <Table.HeaderCell>Action</Table.HeaderCell>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Resource</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {events.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan='8' textAlign='center'>
                    No audit events found
                  </Table.Cell>
                </Table.Row>
              ) : (
                events.map((event) => (
                  <Table.Row key={event.eventId}>
                    <Table.Cell>{formatDateTime(event.timestamp)}</Table.Cell>
                    <Table.Cell>
                      <Label color={getSeverityColor(event.severity)} size='small'>
                        {event.severity}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color='blue' size='small'>
                        {event.category}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label size='small'>{event.action}</Label>
                    </Table.Cell>
                    <Table.Cell>
                      {event.actor?.username || 'N/A'}
                      <br />
                      <span style={{ fontSize: '0.85em', color: '#666' }}>
                        {event.actor?.role || 'N/A'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {event.resource?.type || 'N/A'}
                      <br />
                      <span style={{ fontSize: '0.85em', color: '#666' }}>
                        {event.resource?.name || event.resource?.id || '-'}
                      </span>
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {event.description || 'N/A'}
                    </Table.Cell>
                    <Table.Cell textAlign='center'>
                      <Button
                        icon='eye'
                        color='teal'
                        size='small'
                        onClick={() => handleViewDetails(event)}
                        title='View Details'
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          {/* Pagination */}
          {pageInfo && pageInfo.totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <Pagination
                activePage={currentPage + 1}
                totalPages={pageInfo.totalPages}
                onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
                firstItem={{ content: <Icon name='angle double left' />, icon: true }}
                lastItem={{ content: <Icon name='angle double right' />, icon: true }}
                prevItem={{ content: <Icon name='angle left' />, icon: true }}
                nextItem={{ content: <Icon name='angle right' />, icon: true }}
              />
            </div>
          )}

          <div style={{ marginTop: '20px', color: '#666', textAlign: 'center' }}>
            {pageInfo && (
              <>
                Showing {pageInfo.number * pageInfo.size + 1} -{' '}
                {Math.min((pageInfo.number + 1) * pageInfo.size, pageInfo.totalElements)} of{' '}
                {pageInfo.totalElements} event{pageInfo.totalElements === 1 ? '' : 's'}
              </>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedEvent && (
        <AuditEventDetail
          event={selectedEvent}
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </Container>
  );
}

export default AuditLogViewerPage;
